import { yupResolver } from "@hookform/resolvers/yup";
import * as ContactStepTracker from "@omni/services/tracking/trackers/tools/contact/contactStep.tracker";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import React, { useEffect } from "react";
import { DefaultValues } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";

import {
    AdditionalFAQField,
    parseCategory
} from "@/components/ContactCustomerservice/helpers/category.helpers";
import { FormText } from "@/components/Form/Base/formText.component";
import { FormTouchedCallback } from "@/components/Form/RHF/Callbacks/formFieldCallbacks.component";
import { DefaultFileFieldItem } from "@/components/Form/RHF/FormFields/MultiUploadField/fieldItems/default";
import { FileUploadField } from "@/components/Form/RHF/FormFields/MultiUploadField/fileUploadField.component";
import { MultiFileUploadField } from "@/components/Form/RHF/FormFields/MultiUploadField/multiUploadField.component";
import { PhoneField } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.container";
import { SelectField } from "@/components/Form/RHF/FormFields/SelectField/selectField.component";
import { StoreField } from "@/components/Form/RHF/FormFields/StoreField/storeField.container";
import { TextAreaField } from "@/components/Form/RHF/FormFields/TextAreaField/textAreaField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { getShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import { NumericTextField } from "../Form/RHF/FormFields/NumericTextField/NumericTextfield.component";
import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import { CategoryOption } from "./contactCustomerservice.types";
import {
    ACCEPTED_FILE_TYPES,
    ContactCustomerserviceFormValues,
    ContactCustomerserviceValidationSchema,
    MAX_FILE_UPLOADS
} from "./helpers/formValues.helpers";

const FORM_ID = "contactCustomerserviceForm";

type ContactCustomerserviceProps = {
    onSubmit: (
        formValues: ContactCustomerserviceFormValues,
        reset: (
            newData?: DefaultValues<ContactCustomerserviceFormValues>
        ) => void
    ) => Promise<void>;
    isSubmitting: boolean;
    categoriesFormOptions: Array<CategoryOption>;
    initialFormValues: DefaultValues<ContactCustomerserviceFormValues>;
    loading: boolean;
};

const ContactCustomerservice = ({
    onSubmit,
    isSubmitting,
    initialFormValues,
    categoriesFormOptions,
    loading
}: ContactCustomerserviceProps) => {
    const { t } = useGranite();

    const [additionalFAQField, setAdditionalFAQField] =
        React.useState<AdditionalFAQField>(
            parseCategory(initialFormValues.category)
        );

    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );
    const shopNames = useSelector(getShopNames, shallowEqual);

    const onFormTouched = () => {
        ContactStepTracker.trackStart();
    };

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            category: initialFormValues.category,
            firstName: initialFormValues.firstName,
            lastName: initialFormValues.lastName,
            email: initialFormValues.email,
            phone: initialFormValues.phone,
            bonusCardNumber: initialFormValues.bonusCardNumber,
            storeId: initialFormValues.storeId,
            message: initialFormValues.message,
            phonePrefix: initialFormValues.phonePrefix,
            orderId: ""
        },
        resolver: yupResolver(ContactCustomerserviceValidationSchema),
        context: {
            phonenumberPrefixData: phonenumberPrefixes
        }
    });

    useEffect(() => {
        const callback = methods.subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                setAdditionalFAQField(parseCategory(values.category));
            }
        });

        return () => callback();
    }, [methods.subscribe]);

    const handleSubmit = async (data: ContactCustomerserviceFormValues) => {
        await onSubmit(
            data,
            (newData?: DefaultValues<ContactCustomerserviceFormValues>) =>
                methods.reset(newData)
        );
    };

    return (
        <>
            <Messages messageid={"contact-customerservice-request-success"} />
            <Messages messageid={"contact-customerservice-request-failure"} />{" "}
            <Messages messageid={"contact-customerservice-to-many-failure"} />
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={true}
            >
                {({ initialized }) => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            onSubmit={methods.handleSubmit(handleSubmit)}
                            id={FORM_ID}
                        >
                            <FormTouchedCallback<ContactCustomerserviceFormValues>
                                callback={onFormTouched}
                            />
                            <Container className={cn("px-0")}>
                                <Row>
                                    <Col md={6}>
                                        <SelectField<
                                            ContactCustomerserviceFormValues,
                                            CategoryOption
                                        >
                                            name="category"
                                            label={t(
                                                "general.form.category.label"
                                            )}
                                            placeholder={t(
                                                "general.form.category.placeholder"
                                            )}
                                            autoComplete={"off"}
                                            required={true}
                                            optionList={categoriesFormOptions}
                                            disabled={loading || !initialized}
                                        />
                                    </Col>
                                </Row>
                                {additionalFAQField ===
                                    AdditionalFAQField.OrderRegistryNumber && (
                                    <Row>
                                        <Col>
                                            <TextField<ContactCustomerserviceFormValues>
                                                name="orderId"
                                                label={t(
                                                    "general.form.orderNumber.label"
                                                )}
                                                required={false}
                                                placeholder={t(
                                                    "general.form.orderNumber.placeholder"
                                                )}
                                                disabled={
                                                    loading || !initialized
                                                }
                                            />
                                        </Col>
                                    </Row>
                                )}
                                <Row>
                                    <Col md={6}>
                                        <TextField<ContactCustomerserviceFormValues>
                                            name="firstName"
                                            label={t(
                                                "general.form.firstName.label"
                                            )}
                                            required={false}
                                            autoComplete={"given-name"}
                                            placeholder={t(
                                                "general.form.firstName.placeholder"
                                            )}
                                            disabled={loading || !initialized}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <TextField<ContactCustomerserviceFormValues>
                                            name="lastName"
                                            label={t(
                                                "general.form.lastName.label"
                                            )}
                                            required
                                            autoComplete={"family-name"}
                                            placeholder={t(
                                                "general.form.lastName.placeholder"
                                            )}
                                            disabled={loading || !initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <TextField<ContactCustomerserviceFormValues>
                                            name="email"
                                            label={t(
                                                "general.form.email.label"
                                            )}
                                            required
                                            autoComplete={"email"}
                                            placeholder={t(
                                                "general.form.email.placeholder"
                                            )}
                                            disabled={loading || !initialized}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <PhoneField<ContactCustomerserviceFormValues>
                                            phonenumberPrefixes={
                                                phonenumberPrefixes
                                            }
                                            autoComplete={"tel-national"}
                                            label={t(
                                                "general.form.phoneNumber.label"
                                            )}
                                            placeholder={t(
                                                "general.form.phoneNumber.placeholder"
                                            )}
                                            required={false}
                                            disabled={loading || !initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <NumericTextField<ContactCustomerserviceFormValues>
                                            name="bonusCardNumber"
                                            label={t(
                                                "general.form.bonusCard.label"
                                            )}
                                            placeholder={t(
                                                "general.form.bonusCard.placeholder"
                                            )}
                                            required={false}
                                            disabled={loading || !initialized}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <StoreField<ContactCustomerserviceFormValues>
                                            name="storeId"
                                            label={t(
                                                "general.form.store.label"
                                            )}
                                            placeholder={t(
                                                "general.form.store.placeholder"
                                            )}
                                            required={true}
                                            disabled={loading || !initialized}
                                            storeList={shopNames}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <TextAreaField<ContactCustomerserviceFormValues>
                                            name="message"
                                            label={t(
                                                "general.form.message.label"
                                            )}
                                            required
                                            placeholder={t(
                                                "general.form.message.placeholder"
                                            )}
                                            maxLength={1500}
                                            disabled={loading || !initialized}
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <MultiFileUploadField<ContactCustomerserviceFormValues>
                                            name="attachments"
                                            label={t(
                                                "general.form.attachments.label"
                                            )}
                                            microCopy={({ amountOfFields }) => (
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <FormText
                                                        inline={true}
                                                        color="default"
                                                    >
                                                        {t(
                                                            `form.microcopy.${FORM_ID}.attachments`
                                                        )}{" "}
                                                        <br />
                                                        {t(
                                                            "product.reviews.form.photos.microcopy",
                                                            amountOfFields,
                                                            MAX_FILE_UPLOADS
                                                        )}
                                                    </FormText>
                                                </div>
                                            )}
                                            required={false}
                                            disabled={loading || !initialized}
                                        >
                                            {({
                                                actions,
                                                control,
                                                fields,
                                                name,
                                                disabled
                                            }) => (
                                                <FileUploadField<ContactCustomerserviceFormValues>
                                                    actions={actions}
                                                    control={control}
                                                    fields={fields}
                                                    name={name}
                                                    disabled={disabled}
                                                    maxUploads={
                                                        MAX_FILE_UPLOADS
                                                    }
                                                    acceptedFileTypes={ACCEPTED_FILE_TYPES.flatMap(
                                                        (t) => t.extensions
                                                    )}
                                                    onUploadChange={async (
                                                        file,
                                                        append
                                                    ) => {
                                                        append({
                                                            fileName: file.name,
                                                            fileSize: file.size,
                                                            fileBody: file,
                                                            fileType: file.type,
                                                            url: URL.createObjectURL(
                                                                file
                                                            )
                                                        });
                                                    }}
                                                    renderFieldItem={(
                                                        fileFieldItem
                                                    ) => (
                                                        <DefaultFileFieldItem
                                                            fileName={
                                                                fileFieldItem.fileName
                                                            }
                                                        />
                                                    )}
                                                />
                                            )}
                                        </MultiFileUploadField>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Button
                                            type={"submit"}
                                            loading={isSubmitting}
                                            disabled={loading || !initialized}
                                        >
                                            {t("general.form.send")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Container>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </>
    );
};

export default ContactCustomerservice;
